import { useId } from "react";
import type { CSSProperties, ReactNode } from "react";
import {
  addressValueOf,
  partsAsked,
  problemWords,
  shownParts,
  summaryLines,
  summaryOf,
  valueOf,
  type AddressDraft,
  type Asked,
  type FieldProblem,
  type FieldValue,
  type FormContext,
  type FormDraft,
} from "@libs/buy-on-behalf-client/purchase";
import type { AddressField, AddressPart, Field, Section } from "@libs/platform-wire/purchase";
import { countryData, diallingCode, ISO_COUNTRIES, isoCountryName } from "@libs/platform-wire/world";
import { FieldProblem as ProblemLine } from "../buyer-details";
import { button, input, size, text } from "../ui";
import { Undeliverable } from "../undeliverable";

/** Every country the world module knows, by name. */
const WORLD = ISO_COUNTRIES.filter((country) => countryData(country.code));
/** Every country a phone can be dialled in. */
const DIALLING = ISO_COUNTRIES.filter((country) => diallingCode(country.code));

/** The countries an address field lists, by name: every one it takes, and
 *  every one it refuses (shown with its sentence once chosen). */
const countriesOf = (field: AddressField) =>
  field.countries === "all_accepted"
    ? WORLD
    : field.countries.map((entry) => ({ code: entry.code, name: isoCountryName(entry.code) ?? entry.code }));

/** A field's label as the form says it: "(optional)" where it may be left. */
const labelOf = (label: string, required: boolean) => (required ? label : `${label} (optional)`);

export type SectionProps = {
  section: Section;
  /** Every section of the form: a phone is dialled where the address is. */
  sections: readonly Section[];
  draft: FormDraft;
  context: FormContext;
  /** What the start would refuse, by field. */
  problems: ReadonlyMap<string, readonly FieldProblem[]>;
  /** The fields whose problems are said: left, or tried to continue with. */
  touched: ReadonlySet<string>;
  /** The fields the read's saved answers don't give, asked under the
   *  summary: whole, or an address's missing parts (`askedOf`). */
  asked: Asked;
  /** The buyer pressed Edit: every field is asked. */
  editing: boolean;
  disabled: boolean;
  /** Said under a field (the server's refusal of it). */
  notes?: Readonly<Record<string, ReactNode>>;
  onChange: (fieldId: string, value: FieldValue) => void;
  onBlur: (fieldId: string) => void;
  onEdit: (editing: boolean) => void;
};

/**
 * One section of the purchase's form, drawn from its fields' kinds: what
 * the buyer's saved answers already give is one summary with one Edit, and
 * only the fields they don't give are asked under it. A field's problem is
 * said under it once the buyer has left it. Each label is the field's own.
 */
export function PurchaseSection(props: SectionProps) {
  const { section, editing, disabled } = props;
  // Summarised: what the read's saved answers give, an address's saved
  // parts among them. Decided on the read, so a field never turns into a
  // summary as it is typed in; drawn as it stands now.
  const known = section.fields.filter(
    (field) =>
      props.asked.get(field.id) !== "whole" &&
      summaryOf(field, valueOf(field, {}, props.context, props.sections), partsAsked(props.asked, field) ?? []) !== null,
  );
  const summarised = !editing && known.length > 0;
  // Asked under it: what it doesn't give, and of an address only the parts
  // it lacks. Edit asks every field whole.
  const asked = summarised
    ? section.fields.flatMap((field) => {
        const parts = partsAsked(props.asked, field);
        return !known.includes(field) || parts ? [{ field, parts }] : [];
      })
    : section.fields.map((field) => ({ field, parts: null }));
  const lines = summarised
    ? summaryLines(
        known.flatMap((field) => {
          const line = summaryOf(
            field,
            valueOf(field, props.draft, props.context, props.sections),
            partsAsked(props.asked, field) ?? [],
          );
          return line ? [{ field, line }] : [];
        }),
      )
    : [];
  return (
    <section aria-label={section.title} style={{ display: "grid", gap: "var(--lc-space)" }}>
      <div style={{ alignItems: "baseline", display: "flex", justifyContent: "space-between" }}>
        <span style={{ ...text.strong, fontSize: size.small }}>{section.title}</span>
        {known.length > 0 && editing ? (
          <button
            type="button"
            disabled={disabled}
            onClick={() => props.onEdit(false)}
            style={{ ...button.link, textDecoration: "none" }}
          >
            Done
          </button>
        ) : null}
      </div>
      {section.note ? <p style={text.hint}>{section.note}</p> : null}
      {summarised && lines.length ? (
        <div style={summary}>
          <div style={{ display: "grid", gap: 2, minWidth: 0 }}>
            {lines.map(({ id, line }, index) => (
              <span key={id} style={index === 0 ? text.strong : text.hint}>
                {line}
              </span>
            ))}
          </div>
          <button type="button" aria-label="Edit" disabled={disabled} onClick={() => props.onEdit(true)} style={edit}>
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
              <path
                d="M10.6 2.9 13.1 5.4M2.5 13.5l.6-3.1 7.9-7.9a1.2 1.2 0 0 1 1.7 0l.8.8a1.2 1.2 0 0 1 0 1.7l-7.9 7.9-3.1.6Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      ) : null}
      {summarised
        ? known.map((field) =>
            props.notes?.[field.id] && !partsAsked(props.asked, field) ? <div key={field.id}>{props.notes[field.id]}</div> : null,
          )
        : null}
      {asked.length ? (
        <div style={{ display: "grid", gap: "calc(var(--lc-space) * 1.25)" }}>
          {asked.map(({ field, parts }) => (
            <FieldControl key={field.id} field={field} parts={parts} {...props} />
          ))}
        </div>
      ) : null}
    </section>
  );
}

/** One field, by its kind, with what is said under it: of an address, only
 *  `parts` where they are given. */
function FieldControl({ field, parts, ...props }: { field: Field; parts: readonly AddressPart[] | null } & SectionProps) {
  const value = valueOf(field, props.draft, props.context, props.sections);
  // A refused country is said once, at once, by the host's note under the
  // field (the seller's sentence); every other problem once the field is
  // left.
  const said = props.touched.has(field.id)
    ? (props.problems.get(field.id) ?? []).filter((problem) => problem.code !== "country_refused").flatMap((problem) => {
        const words = problemWords(field, problem, value);
        return words ? [words] : [];
      })
    : [];
  const note = props.notes?.[field.id];
  const change = (next: FieldValue) => props.onChange(field.id, next);
  const blur = () => props.onBlur(field.id);
  return (
    <div style={{ display: "grid", gap: 6 }} data-field={field.id}>
      <Control field={field} value={value} parts={parts} change={change} blur={blur} {...props} />
      {field.note ? <p style={text.hint}>{field.note}</p> : null}
      {said.map((words) => (
        <ProblemLine key={words}>{words}</ProblemLine>
      ))}
      {note}
    </div>
  );
}

function Control({
  field,
  value,
  parts,
  change,
  blur,
  disabled,
  draft,
  context,
}: {
  field: Field;
  value: FieldValue;
  parts: readonly AddressPart[] | null;
  change: (value: FieldValue) => void;
  blur: () => void;
} & SectionProps) {
  const ids = useId();
  switch (field.kind) {
    case "text":
      return (
        <label style={labelStyle}>
          <span>{labelOf(field.label, field.required)}</span>
          <input
            style={look}
            type={field.input === "email" ? "email" : "text"}
            inputMode={field.input === "numeric" ? "numeric" : field.input === "email" ? "email" : undefined}
            autoComplete={field.autoComplete ?? undefined}
            required={field.required}
            maxLength={field.maxLength}
            value={value.kind === "text" ? value.value : ""}
            disabled={disabled}
            onBlur={blur}
            onChange={(event) => change({ kind: "text", value: event.target.value })}
          />
        </label>
      );
    case "choice":
      return (
        <label style={labelStyle}>
          <span>{labelOf(field.label, field.required)}</span>
          <select
            style={look}
            value={value.kind === "choice" ? value.value : ""}
            disabled={disabled}
            onBlur={blur}
            onChange={(event) => change({ kind: "choice", value: event.target.value })}
          >
            <option value="" disabled={field.required}>
              {field.required ? "Select" : "None"}
            </option>
            {field.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      );
    case "date":
      return (
        <label style={labelStyle}>
          <span>{labelOf(field.label, field.required)}</span>
          {field.precision === "day" ? (
            <input
              style={look}
              type="date"
              autoComplete="bday"
              min={field.earliest}
              max={new Date().toISOString().slice(0, 10)}
              required={field.required}
              value={value.kind === "date" ? value.value : ""}
              disabled={disabled}
              onBlur={blur}
              onChange={(event) => change({ kind: "date", value: event.target.value })}
            />
          ) : (
            <input
              style={look}
              type="text"
              inputMode="numeric"
              autoComplete="bday-year"
              maxLength={4}
              placeholder="YYYY"
              required={field.required}
              value={value.kind === "date" ? value.value : ""}
              disabled={disabled}
              onBlur={blur}
              onChange={(event) => change({ kind: "date", value: event.target.value.replace(/\D/g, "") })}
            />
          )}
        </label>
      );
    case "phone": {
      const phone = value.kind === "phone" ? value : { kind: "phone" as const, country: "", number: "" };
      return (
        <div style={labelStyle}>
          <span id={`${ids}-label`}>{labelOf(field.label, field.required)}</span>
          <div style={{ display: "flex", gap: 8, minWidth: 0 }}>
            <span style={{ ...look, ...dialling }}>
              <span aria-hidden="true">{diallingCode(phone.country) ?? "+"}</span>
              <select
                aria-label="Country code"
                value={phone.country}
                disabled={disabled}
                onChange={(event) => change({ ...phone, country: event.target.value })}
                style={hiddenSelect}
              >
                <option value="" disabled>
                  Select country
                </option>
                {DIALLING.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name} ({diallingCode(country.code)})
                  </option>
                ))}
              </select>
            </span>
            <input
              aria-labelledby={`${ids}-label`}
              style={{ ...look, flex: "1 1 0%", minWidth: 0, width: "auto" }}
              type="tel"
              inputMode="tel"
              autoComplete="tel-national"
              required={field.required}
              maxLength={40}
              value={phone.number}
              disabled={disabled}
              onBlur={blur}
              onChange={(event) => change({ ...phone, number: event.target.value })}
            />
          </div>
        </div>
      );
    }
    case "address":
      return (
        <AddressControl
          field={field}
          value={value.kind === "address" ? value.value : addressValueOf(field, draft, context)}
          only={parts}
          change={(next) => change({ kind: "address", value: next })}
          blur={blur}
          disabled={disabled}
        />
      );
  }
}

/** An address: its country, then the parts the seller asks as the country
 *  has them (`formFor`), each labelled in the country's words. A country
 *  change keeps what was typed of the parts both countries have. Asked
 *  under a summary of its saved parts, only the parts it lacks (`only`),
 *  without its country. */
function AddressControl({
  field,
  value,
  only,
  change,
  blur,
  disabled,
}: {
  field: AddressField;
  value: AddressDraft;
  only: readonly AddressPart[] | null;
  change: (value: AddressDraft) => void;
  blur: () => void;
  disabled: boolean;
}) {
  const parts = (value.country ? shownParts(field, value.country) : []).filter(
    (part) => !only || only.includes(part.part),
  );
  return (
    <div style={{ display: "grid", gap: "calc(var(--lc-space) * 1.25)" }}>
      {parts
        .filter((part) => part.part !== "region" && part.part !== "postcode")
        .map((part) => (
          <PartInput key={part.part} part={part} value={value} change={change} blur={blur} disabled={disabled} />
        ))}
      {parts
        .filter((part) => part.part === "region" || part.part === "postcode")
        .map((part) => (
          <PartInput key={part.part} part={part} value={value} change={change} blur={blur} disabled={disabled} />
        ))}
      {only ? null : (
        <label style={labelStyle}>
          <span>{field.label}</span>
          <select
            style={look}
            value={value.country}
            disabled={disabled}
            onBlur={blur}
            // The region is the country's: another country's is dropped.
            onChange={(event) => change({ ...value, country: event.target.value, region: "" })}
          >
            <option value="" disabled>
              Select country
            </option>
            {countriesOf(field).map((country) => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
}

function PartInput({
  part,
  value,
  change,
  blur,
  disabled,
}: {
  part: ReturnType<typeof shownParts>[number];
  value: AddressDraft;
  change: (value: AddressDraft) => void;
  blur: () => void;
  disabled: boolean;
}) {
  const label = labelOf(part.label, part.required);
  if (part.part === "region" && "options" in part && part.options)
    return (
      <label style={labelStyle}>
        <span>{label}</span>
        <select
          style={look}
          autoComplete="address-level1"
          value={value.region}
          disabled={disabled}
          onBlur={blur}
          onChange={(event) => change({ ...value, region: event.target.value })}
        >
          <option value="" disabled={part.required}>
            {part.required ? `Select ${part.label.toLowerCase()}` : "None"}
          </option>
          {part.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    );
  return (
    <label style={labelStyle}>
      <span>{label}</span>
      <input
        style={look}
        type="text"
        autoComplete={AUTOCOMPLETE[part.part]}
        required={part.required}
        maxLength={240}
        value={value[part.part]}
        disabled={disabled}
        onBlur={blur}
        onChange={(event) => change({ ...value, [part.part]: event.target.value })}
      />
    </label>
  );
}

const AUTOCOMPLETE = {
  line1: "address-line1",
  line2: "address-line2",
  city: "address-level2",
  region: "address-level1",
  postcode: "postal-code",
} as const;

/** What the form says when the chosen country is one the seller refuses. */
export function CountryRefused({ children }: { children: ReactNode }) {
  return <Undeliverable>{children}</Undeliverable>;
}

const labelStyle: CSSProperties = { ...text.label, display: "grid", gap: "calc(var(--lc-space) * 0.5)" };

const look: CSSProperties = { ...input, fontSize: "var(--lc-input-font-size)", outline: "none" };

const dialling: CSSProperties = {
  alignItems: "center",
  display: "inline-flex",
  flex: "0 0 auto",
  justifyContent: "center",
  minWidth: 72,
  position: "relative",
  width: "auto",
};

const hiddenSelect: CSSProperties = {
  cursor: "pointer",
  font: "inherit",
  height: "100%",
  inset: 0,
  opacity: 0,
  position: "absolute",
  width: "100%",
};

const summary: CSSProperties = {
  ...input,
  alignItems: "center",
  display: "grid",
  gap: "var(--lc-space)",
  gridTemplateColumns: "minmax(0, 1fr) auto",
  padding: "calc(var(--lc-space) * 1.25) calc(var(--lc-space) * 1.5)",
};

const edit: CSSProperties = {
  ...button.link,
  alignItems: "center",
  display: "inline-flex",
  height: 32,
  justifyContent: "center",
  margin: "-4px calc(var(--lc-space) * -0.75) -4px 0",
  width: 32,
};
